// Service photos. Each work (a child row of a vessel in the Service Smartsheet) can carry up to five photos of
// the yard's own, and each vessel up to five the client sent (of the boat, not of one work), each with a note. They live in Smartsheet only,
// never in D1 or R2: a photo is one row discussion whose single comment holds the note ("📷 Rust on the
// cleat", or for the client's "📩 From the client: Scratch on the hull"), with the image attached to that
// comment. An image attached straight to the row in Smartsheet counts as one of the yard's (no note).
// The worker (routes/servicePhotos.ts) and the web app (features/servicePhotos.ts) share this module.
import { z } from 'zod';
/** photos per row, of each source (a work's from the yard, a vessel's from the client); the worker refuses the sixth upload. */
export const SERVICE_PHOTO_MAX = 5;
/** whose photo it is: taken by the yard, or sent by the client */
export const SERVICE_PHOTO_SOURCES = ['yard', 'client'];
/** the largest upload the worker takes (Smartsheet's own limit is 30 MB). */
export const SERVICE_PHOTO_MAX_BYTES = 10 * 1024 * 1024;
/** the longest note, in characters. */
export const SERVICE_PHOTO_NOTE_MAX = 500;
/** the marker that starts a photo comment in Smartsheet. */
export const SERVICE_PHOTO_MARK = '📷';
/** the marker that starts a client photo's comment, and the words after it */
export const SERVICE_CLIENT_PHOTO_MARK = '📩';
const CLIENT_WORDS = 'From the client';
/** the comment text of a photo saved without a note. */
const NO_NOTE = 'Photo';
/** image types accepted as photos. SVG is left out on purpose: it is a document that can run script,
 *  and the file route serves the bytes from the app's own origin. */
export const SERVICE_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif', 'image/avif'];
/** whether a MIME type is one of the photo types (case and parameters ignored). */
export function isServicePhotoType(mime) {
    const t = String(mime ?? '').split(';')[0].trim().toLowerCase();
    return SERVICE_PHOTO_TYPES.includes(t === 'image/jpg' ? 'image/jpeg' : t);
}
/** the Smartsheet comment text for a note: "📷 Rust on the cleat", or "📷 Photo" with no note; a client's
 *  "📩 From the client: Scratch on the hull", or "📩 From the client". */
export function photoCommentText(note, source = 'yard') {
    const n = note.trim();
    if (source === 'client')
        return `${SERVICE_CLIENT_PHOTO_MARK} ${CLIENT_WORDS}${n ? `: ${n}` : ''}`;
    return `${SERVICE_PHOTO_MARK} ${n || NO_NOTE}`;
}
/** whose photo a comment is: the client's when it starts with their marker */
export function photoSourceOfComment(text) {
    return String(text ?? '').trim().startsWith(SERVICE_CLIENT_PHOTO_MARK) ? 'client' : 'yard';
}
/** the note a comment carries: the marker (and a client's "From the client") is dropped, and the bare
 *  "📷 Photo" reads as no note. A comment written in Smartsheet without a marker is the note as it stands. */
export function photoNoteFromComment(text) {
    const t = String(text ?? '').trim();
    if (t.startsWith(SERVICE_CLIENT_PHOTO_MARK)) {
        const rest = t.slice(SERVICE_CLIENT_PHOTO_MARK.length).replace(/^️/, '').trim();
        return rest.toLowerCase().startsWith(CLIENT_WORDS.toLowerCase()) ? rest.slice(CLIENT_WORDS.length).replace(/^\s*:\s*/, '').trim() : rest;
    }
    if (!t.startsWith(SERVICE_PHOTO_MARK))
        return t;
    const n = t.slice(SERVICE_PHOTO_MARK.length).replace(/^️/, '').trim();
    return n === NO_NOTE ? '' : n;
}
/** whether a comment is one the app wrote for a photo (the yard's or a client's). */
export function isPhotoComment(text) {
    const t = String(text ?? '').trim();
    return t.startsWith(SERVICE_PHOTO_MARK) || t.startsWith(SERVICE_CLIENT_PHOTO_MARK);
}
const Id = z.number().int().positive();
export const ServicePhotoNoteSchema = z.object({
    rowId: Id,
    commentId: Id,
    note: z.string().max(SERVICE_PHOTO_NOTE_MAX * 2).transform((s) => s.trim()).pipe(z.string().max(SERVICE_PHOTO_NOTE_MAX)),
});
export const ServicePhotoDeleteSchema = z.object({ rowId: Id, id: Id });
//# sourceMappingURL=servicePhoto.js.map